import type { ReactNode } from "react";
import { Link } from "react-router-dom";

type Variant = "primary" | "accent" | "outline" | "outlineLight" | "dark" | "ghost";
type Size = "sm" | "md" | "lg";

interface ActionButtonProps {
  children: ReactNode;
  to?: string;
  href?: string;
  variant?: Variant;
  size?: Size;
  className?: string;
  icon?: string;
  iconRight?: string;
  type?: "button" | "submit";
  onClick?: () => void;
  disabled?: boolean;
  external?: boolean;
}

const variantClasses: Record<Variant, string> = {
  primary:
    "bg-primary-500 text-foreground-950 hover:bg-primary-400 border border-transparent",
  accent:
    "bg-accent-500 text-foreground-950 hover:bg-accent-400 border border-transparent",
  outline:
    "border border-foreground-300 text-foreground-900 hover:border-primary-500 hover:text-primary-700 bg-transparent",
  outlineLight:
    "border border-background-50/40 text-background-50 hover:bg-background-50/10 hover:border-background-50/70 bg-transparent",
  dark: "bg-foreground-950 text-background-50 hover:bg-foreground-800 border border-transparent",
  ghost: "text-foreground-700 hover:text-primary-700 bg-transparent",
};

const sizeClasses: Record<Size, string> = {
  sm: "px-3.5 py-2 text-xs",
  md: "px-5 py-2.5 text-sm",
  lg: "px-6 py-3.5 text-sm md:text-base",
};

export default function ActionButton({
  children,
  to,
  href,
  variant = "primary",
  size = "md",
  className = "",
  icon,
  iconRight,
  type = "button",
  onClick,
  disabled,
  external,
}: ActionButtonProps) {
  const base = `inline-flex items-center justify-center gap-2 rounded-md font-label font-semibold whitespace-nowrap transition-colors duration-200 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 ${variantClasses[variant]} ${sizeClasses[size]} ${className}`;

  const inner = (
    <>
      {icon ? (
        <span className="flex h-4 w-4 items-center justify-center">
          <i className={`${icon} text-base`} />
        </span>
      ) : null}
      {children}
      {iconRight ? (
        <span className="flex h-4 w-4 items-center justify-center">
          <i className={`${iconRight} text-base`} />
        </span>
      ) : null}
    </>
  );

  if (to) {
    return (
      <Link to={to} className={base}>
        {inner}
      </Link>
    );
  }

  if (href) {
    return (
      <a
        href={href}
        className={base}
        target={external ? "_blank" : undefined}
        rel={external ? "nofollow noopener noreferrer" : undefined}
      >
        {inner}
      </a>
    );
  }

  return (
    <button type={type} onClick={onClick} disabled={disabled} className={base}>
      {inner}
    </button>
  );
}